import printJS from "print-js";
import { KUBER_LOGO_BASE64 } from "constants/kuber-logo-base64";
import {
  DEFAULT_PRINT_LAYOUT_SETTINGS,
  PRINT_LAYOUT_STORAGE_KEY,
  getStoredPrintLayoutSettings,
  getWrapperPaperWidth,
  normalizePrintLayoutSettings,
} from "./printLayoutSettings";
import { printLayoutToEditorState, resolvePrintLayout } from "./printLayoutApi";

const formatMoney = (value) =>
  `Rp. ${parseInt(value || 0).toLocaleString("id-ID")}`;

const escapeHtml = (value) =>
  String(value ?? "-")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");

const getGuestName = (booking) =>
  booking.guest_name || booking.customer?.name || "-";

const getHotelName = (booking) => booking.customer?.nationality || "-";

const getTrackLabel = (booking) =>
  booking.track === "A" ? "PREMIUM" : "REGULAR";

const getActivityName = (activity) => {
  const primary = activity?.name || activity?.activity;
  const fallback = activity?.activity || activity?.name;
  if (primary && !["Activity Name", "Activity Header"].includes(String(primary).trim())) {
    return primary;
  }
  return fallback || "-";
};

const FORCED_IMAGE_WIDTH = 384;
const FORCED_LOGO_WIDTH = 150;

// Setting aktif selama satu proses print (hasil API + lokal); null = baca lokal.
let activeLayoutSettings = null;
const getLayoutSettings = () => activeLayoutSettings || getStoredPrintLayoutSettings();

const REMOTE_LAYOUT_TIMEOUT_MS = 3000;
const remoteLayoutCacheKey = (printType) => `${PRINT_LAYOUT_STORAGE_KEY}:remote:${printType}`;

const apiPrintTypeFor = (options) => {
  if (options.type !== "ticket") return "guest_invoice";
  return options.printTickets?.activity === false ? "meal" : "ticket";
};

// Buang nilai kosong supaya setting yang tidak ada di API tetap memakai setting lokal.
const compactSettings = (settings) =>
  Object.fromEntries(
    Object.entries(settings || {}).filter(
      ([, value]) =>
        value !== undefined &&
        value !== null &&
        value !== "" &&
        !(Array.isArray(value) && value.length === 0)
    )
  );

const readRemoteLayoutCache = (printType) => {
  try {
    return JSON.parse(window.localStorage.getItem(remoteLayoutCacheKey(printType)) || "null");
  } catch (error) {
    return null;
  }
};

const writeRemoteLayoutCache = (printType, settings) => {
  try {
    if (settings) {
      window.localStorage.setItem(remoteLayoutCacheKey(printType), JSON.stringify(settings));
    } else {
      window.localStorage.removeItem(remoteLayoutCacheKey(printType));
    }
  } catch (error) {
    // Cache hanya cadangan offline; gagal simpan tidak menghentikan print.
  }
};

// Prioritas: layout API untuk tipe print ini -> cache API terakhir (bila offline) -> setting lokal.
const resolveLayoutSettings = async (printType) => {
  const localSettings = getStoredPrintLayoutSettings();
  let remoteSettings;
  try {
    const layout = await Promise.race([
      resolvePrintLayout(printType),
      new Promise((_, reject) =>
        setTimeout(() => reject(new Error("Print layout API timeout")), REMOTE_LAYOUT_TIMEOUT_MS)
      ),
    ]);
    remoteSettings = layout ? compactSettings(printLayoutToEditorState(layout)) : null;
    writeRemoteLayoutCache(printType, remoteSettings);
  } catch (error) {
    remoteSettings = readRemoteLayoutCache(printType);
  }
  return remoteSettings
    ? normalizePrintLayoutSettings({ ...localSettings, ...remoteSettings })
    : localSettings;
};

const getLogoSource = () => {
  const logo = getLayoutSettings().logoDataUrl || KUBER_LOGO_BASE64;
  if (!logo || typeof logo !== "string") return KUBER_LOGO_BASE64;
  if (logo.startsWith("data:") || logo.startsWith("http://") || logo.startsWith("https://")) return logo;
  const mime = logo.startsWith("iVBOR") ? "image/png" : logo.startsWith("R0lG") ? "image/gif" : "image/jpeg";
  return `data:${mime};base64,${logo}`;
};

const getConfiguredItemStyle = (id) =>
  getLayoutSettings().itemStyles?.find((item) => item.id === id);

// Thermal wrapper hanya mengenal kelas ukuran: small (Font B), normal, dan large
// (tinggi + lebar ganda, kapasitas baris jadi setengah).
const commandSizeFor = (fontSize, fallback, { allowSmall = false } = {}) => {
  const size = Number(fontSize);
  if (size >= 15) return "large";
  if (allowSmall && Number.isFinite(size) && size < 10) return "small";
  return fallback;
};

const commandLineHeight = () => {
  const lineHeight = Number(getLayoutSettings().lineHeight);
  return Math.min(3, Math.max(0.5, Number.isFinite(lineHeight) ? lineHeight : 1.1));
};

const getPaperConfig = () => {
  // Kertas yang dipasang di APK lebih dipercaya daripada setting web.
  const paperWidth =
    getWrapperPaperWidth() ?? (Number(getLayoutSettings().paperWidth) >= 70 ? 80 : 58);
  return {
    paperSize: `${paperWidth}mm`,
    width: paperWidth === 80 ? 576 : FORCED_IMAGE_WIDTH,
  };
};

const getLogoBase64Payload = () => {
  const configuredLogo = getLayoutSettings().logoDataUrl || KUBER_LOGO_BASE64;
  if (!configuredLogo || typeof configuredLogo !== "string") return "";
  // JSON wrapper membutuhkan payload base64; URL API dipakai oleh fallback HTML.
  if (!configuredLogo.startsWith("data:")) return getLogoBase64PayloadFromDefault();
  const parts = configuredLogo.split(",");
  return parts.length === 2 ? parts[1] : configuredLogo;
};

const getLogoBase64PayloadFromDefault = () => {
  if (!KUBER_LOGO_BASE64 || typeof KUBER_LOGO_BASE64 !== "string") return "";
  const parts = KUBER_LOGO_BASE64.split(",");
  return parts.length === 2 ? parts[1] : KUBER_LOGO_BASE64;
};

const preloadPrintImage = (src) =>
  new Promise((resolve) => {
    if (!src || typeof Image === "undefined") {
      resolve();
      return;
    }

    const img = new Image();
    img.onload = resolve;
    img.onerror = resolve;
    img.src = src;
  });

const getPrintMeta = () => {
  const today = new Date();
  return {
    date: today.toLocaleDateString("id-ID", {
      day: "numeric",
      month: "short",
      year: "numeric",
    }),
    time: today.toLocaleTimeString("id-ID", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    }),
  };
};

const invoiceCss = `
  @page { size: 58mm auto; margin: 0; }
  body {
    font-family: Arial, sans-serif;
    width: 58mm;
    margin: 0;
    padding: 4px;
    color: #000;
    font-weight: 400;
  }
  .receipt {
    text-align: center;
    width: 100%;
    font-size: 10px;
    line-height: 1.15;
  }
  .receipt p {
    margin: 2px 0;
  }
  p {
    margin: 2px 0;
  }
  .border-dashed {
    border-top: 1px dashed #000;
    margin: 4px 0;
  }
  table {
    width: 100%;
    font-size: 14px;
    border-collapse: collapse;
  }
  td, th {
    padding: 2px 5px;
    text-align: left;
    vertical-align: top;
  }
  strong {
    font-weight: 700;
  }
  .detail-table {
    border-collapse: separate;
    border-spacing: 0 .25px;
  }
  .detail-table td {
    padding: 1px 4px 1.25px;
  }
  .detail-label {
    font-weight: 800;
    text-align: left;
    width: 38%;
    padding-right: 4px;
  }
  .detail-value {
    text-align: right;
    width: 62%;
    word-break: break-word;
  }
  .payment-table {
    width: 100%;
    border-collapse: collapse;
    table-layout: auto;
  }
  .payment-table td {
    padding: 1px 4px 1.25px;
  }
  .payment-label {
    font-weight: 800;
    text-align: left;
    padding-right: 6px;
    white-space: nowrap;
  }
  .payment-value {
    text-align: right;
    white-space: nowrap;
    word-break: normal;
  }
  .right {
    text-align: right;
  }
  .center {
    text-align: center;
  }
  .track-title {
    text-align: center;
    font-size: 35px;
    margin: 3px 0;
    font-weight: 700;
  }
  .activity-row {
    font-size: 14px;
    font-weight: 400;
  }
  .activity-table td,
  .activity-table th {
    padding: 1px 4px;
  }
  .payment-muted td {
    font-size: 14px;
    padding: 1px 4px;
  }
  .payment-total td {
    font-size: 14px;
    line-height: 1.15;
    padding-top: 2px;
    font-weight: 400;
  }
  .payment-total .payment-label {
    font-weight: 800;
  }
  .thank-you {
    margin-bottom: 0 !important;
    padding-bottom: 0 !important;
    line-height: 1 !important;
  }
  .print-logo-image {
    margin: 0 0 1px;
    line-height: 0;
  }
  .signature-title {
    margin-top: 0 !important;
    margin-bottom: 1px !important;
  }
  .border-dashed.signature-divider,
  .border-dashed.footer-divider {
    margin: 0;
  }
  .signature-space {
    height: 60px;
  }
  .print-logo {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 2px;
    margin-bottom: 4px;
    font-size: 6px;
    line-height: 1;
  }
  .print-logo-mark {
    font-size: 8px;
    font-weight: 700;
  }
  .print-logo-text {
    font-size: 5px;
    font-weight: 700;
    letter-spacing: 0.2px;
  }
`;

const ticketCss = `
  @page { size: 58mm auto; margin: 0; }
  body {
    font-family: Arial, sans-serif;
    text-align: center;
    width: 58mm;
    margin: 0;
    padding: 4px;
    color: #000;
    font-weight: 400;
  }
  table {
    width: 100%;
    border-collapse: collapse;
    font-size: 14px;
  }
  th, td {
    padding: 1px 4px;
    text-align: left;
    vertical-align: top;
  }
  .detail-label {
    font-weight: 800;
    text-align: left;
    width: 38%;
    padding-right: 4px;
  }
  .detail-value {
    text-align: right;
    width: 62%;
    word-break: break-word;
  }
  .payment-table {
    width: 100%;
    border-collapse: collapse;
    table-layout: auto;
  }
  .payment-table td {
    padding: 1px 4px 1.25px;
  }
  .payment-label {
    font-weight: 800;
    text-align: left;
    padding-right: 6px;
    white-space: nowrap;
  }
  .payment-value {
    text-align: right;
    white-space: nowrap;
    word-break: normal;
  }
  .payment-total td {
    font-size: 12px;
    line-height: 1.15;
    padding-top: 2px;
    font-weight: 400;
  }
  .payment-total .payment-label {
    font-weight: 800;
  }
  .detail-table {
    border-collapse: separate;
    border-spacing: 0 .25px;
  }
  .detail-table td {
    padding: 1px 4px 1.25px;
  }
  .title {
    font-size: 16px;
    font-weight: bold;
    margin: 4px 0;
  }
  .label {
    font-size: 14px;
    font-weight: bold;
    text-transform: uppercase;
    margin: 0.67em 0;
  }
  .track {
    font-size: 35px;
    font-weight: bold;
    margin: 3px 0;
  }
  .border-dashed {
    border-bottom: 1px dashed #000;
    margin: 4px 0;
  }
  .text-right, .right {
    text-align: right;
  }
  .text-center, .center {
    text-align: center;
  }
  .text-left, .left {
    text-align: left;
  }
  .small {
    font-size: 9px;
  }
  .ticket-company {
    font-size: 6px;
    line-height: 1.2;
    margin: 2px 0 0;
  }
  .main-activity {
    font-size: 14px;
    font-weight: 400;
  }
  .meal-voucher-table {
    margin-top: 0;
    table-layout: fixed;
  }
  .meal-voucher-table th,
  .meal-voucher-table td {
    padding: 1px 4px;
  }
  .meal-voucher-table th {
    border-bottom: 0;
    font-size: 14px;
    font-weight: 700;
  }
  .meal-item {
    font-size: 14px;
    font-weight: 400;
    line-height: 1.15;
    width: 82%;
  }
  .meal-qty {
    font-size: 14px;
    font-weight: 400;
    line-height: 1.15;
    text-align: right;
    vertical-align: top;
    white-space: nowrap;
    width: 18%;
  }
  .meal-note {
    border-bottom: 0;
    font-size: 12px;
    padding-top: 0 !important;
    text-align: left;
  }
  .ticket-footer {
    font-size: 9px;
    font-weight: 700;
    line-height: 1;
    margin: 5px 0 0;
    padding-bottom: 0;
    text-align: center;
  }
  .thank-you {
    margin-bottom: 0 !important;
    padding-bottom: 0 !important;
    line-height: 1 !important;
  }
  .print-logo-image {
    margin: 0 0 1px;
    line-height: 0;
  }
  .signature-title {
    margin-top: 0 !important;
    margin-bottom: 1px !important;
  }
  .border-dashed.signature-divider,
  .border-dashed.footer-divider {
    margin: 0;
  }
  .print-logo {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 2px;
    margin-bottom: 4px;
    font-size: 6px;
    line-height: 1;
  }
  .print-logo-mark {
    font-size: 8px;
    font-weight: 700;
  }
  .print-logo-text {
    font-size: 5px;
    font-weight: 700;
    letter-spacing: 0.2px;
  }
`;

const wrapPrintHtml = (title, css, pages) => {
  const html = pages
    .map((page) => `<div class="print-page">${page}</div>`)
    .join("");
  const fullCss = `
    ${css}
    .print-page:not(:last-child) {
      page-break-after: always;
    }
    .print-page {
      margin-bottom: 0;
      padding-bottom: 0;
    }
  `;
  return { html, css: fullCss };
};

const renderPrintLogo = () => {
  return `
  <div class="print-logo-image" style="text-align: center;">
    <img src="${getLogoSource()}" alt="Kuber Logo" style="width: 56px; height: auto; max-width: 100%;" />
  </div>
`;
};

const renderInvoiceHeader = () => {
  const settings = getLayoutSettings();
  return `
  <div class="receipt">
    ${renderPrintLogo()}
    <p style="font-size:${settings.headerFontSize}px"><strong>${escapeHtml(settings.companyName)}</strong></p>
    <p style="font-size:${settings.companyFontSize}px">${escapeHtml(settings.companyAddress)}</p>
    <div class="border-dashed"></div>
  </div>
`;
};

const renderTicketHeader = (label) => {
  const settings = getLayoutSettings();
  return `
  <div class="text-center">
    <h1 class="label" style="font-size:${settings.titleFontSize}px">${escapeHtml(label)}</h1>
    ${renderPrintLogo()}
    <p class="ticket-company" style="font-size:${settings.companyFontSize}px">${escapeHtml(settings.companyAddress)}</p>
  </div>
`;
};

const renderDetailRows = (rows) =>
  rows
    .map(
      ([label, value, _strongLabel = true, upperValue = false]) => `
        <tr class="detail-row">
          <td class="detail-label">${escapeHtml(label)}</td>
          <td class="detail-value right text-right" style="${upperValue ? "text-transform: uppercase;" : ""}">
            ${escapeHtml(value)}
          </td>
        </tr>
      `
    )
    .join("");

const renderConfiguredIdentityRows = (booking, printMeta, user, includeHost = true) => {
  const values = {
    host: user?.name || "System",
    code: booking.code || "-",
    date: booking.reservation_date || "-",
    print_date: printMeta.date,
    print_time: printMeta.time,
    guest: booking.customer?.name || getGuestName(booking),
    hotel: getHotelName(booking),
    agent: booking.agent?.name || "-",
    driver: booking.driver || "-",
    transport: booking.guest_transport || "-",
    total_pax: booking.guest_pax || "-",
  };
  const configuredFields = getLayoutSettings().fields;
  if (!Array.isArray(configuredFields)) {
    return renderDetailRows(Object.entries(values).filter(([id]) => includeHost || id !== "host"));
  }
  return configuredFields
    .filter((field) => field.visible && values[field.id] !== undefined && (includeHost || field.id !== "host"))
    .map((field) => `
      <tr class="detail-row">
        <td class="detail-label" style="font-weight:${field.bold === false ? 400 : 700};font-size:${field.fontSize}px">${escapeHtml(field.label || field.id)}</td>
        <td class="detail-value right text-right" style="font-size:${field.fontSize}px;text-align:${field.align || "right"};">${escapeHtml(values[field.id])}</td>
      </tr>
    `)
    .join("");
};

const renderInvoiceActivities = (activities, showAmount = false) => `
  <table class="activity-table">
    <thead>
      <tr>
        <th>Activity</th>
        <th class="right">${showAmount ? "Qty / Amount" : "Qty"}</th>
      </tr>
    </thead>
    <tbody>
      ${(activities || [])
        .map((activity) => {
          const amount = Number(activity.price || 0) * Number(activity.qty || 0);
          const qty = showAmount
            ? `${activity.qty || 0} / ${formatMoney(amount)}`
            : activity.qty || 0;
          return `
            <tr>
              <td class="activity-row">${escapeHtml(getActivityName(activity))}</td>
              <td class="right activity-row">${escapeHtml(qty)}</td>
            </tr>
          `;
        })
        .join("")}
    </tbody>
  </table>
`;

const renderTicketActivities = (activities) => `
  <div class="border-dashed"></div>
  <h3 class="title">Main Activities</h3>
  <table class="activity-table">
    <tr><th>Activity</th><th class="text-right">Qty</th></tr>
    ${(activities || [])
      .map(
        (activity) => `
          <tr>
            <td class="main-activity">${escapeHtml(getActivityName(activity))}</td>
            <td class="text-right">${escapeHtml(activity.qty || 0)}</td>
          </tr>
        `
      )
      .join("")}
  </table>
`;

const renderPaymentRows = (booking, subtotal) => {
  if (booking.payment_splits?.length) {
    let totalPaid = 0;
    const splitRows = booking.payment_splits
      .map((split, index) => {
        const baseAmount = parseInt(split.amount || 0);
        const taxAmount = (baseAmount * parseInt(split.tax || 0)) / 100;
        const totalAmount = baseAmount + taxAmount;
        totalPaid += totalAmount;

        return `
          <tr>
            <td class="payment-label">Payment #${index + 1}</td>
            <td class="payment-value right">${escapeHtml(split.payment_method?.name || "Unknown")}</td>
          </tr>
          <tr class="payment-muted">
            <td class="payment-label">Amount</td>
            <td class="payment-value right">${escapeHtml(formatMoney(totalAmount))}</td>
          </tr>
        `;
      })
      .join("");

    return `
      <tr><td class="payment-label">Total Guest</td><td class="payment-value right">${escapeHtml(booking.guest_pax || "-")}</td></tr>
      <tr><td class="payment-label">Payment Type</td><td class="payment-value right">SPLIT PAYMENT</td></tr>
      ${splitRows}
      <tr class="payment-total"><td class="payment-label">Total Paid</td><td class="payment-value right">${escapeHtml(formatMoney(totalPaid))}</td></tr>
    `;
  }

  const taxPercentage = parseInt(booking.tax || 0);
  const taxAmount = (parseInt(subtotal || 0) * taxPercentage) / 100;
  const totalPaid = parseInt(subtotal || 0) + taxAmount;

  return `
    <tr><td class="payment-label">Total Guest</td><td class="payment-value right">${escapeHtml(booking.guest_pax || "-")}</td></tr>
    <tr><td class="payment-label">Payment Method</td><td class="payment-value right">${escapeHtml(booking.payment_method?.name || "-")}</td></tr>
    <tr class="payment-muted"><td class="payment-label">Sub Total</td><td class="payment-value right">${escapeHtml(formatMoney(subtotal))}</td></tr>
    <tr class="payment-muted"><td class="payment-label">Tax ${taxPercentage || "-"}%</td><td class="payment-value right">${escapeHtml(formatMoney(taxAmount))}</td></tr>
    <tr class="payment-total"><td class="payment-label">Total Paid</td><td class="payment-value right">${escapeHtml(formatMoney(totalPaid))}</td></tr>
  `;
};

const buildInvoiceHtml = (booking, options, printMeta) => {
  const { type = "invoice", user = { name: "Host" }, activities = [] } = options;
  let itemsToPrint = booking.activities || [];

  if (type === "activity") {
    itemsToPrint = itemsToPrint.filter(
      (item) => item.type === "product" || item.activity === "Rafting"
    );
  }
  if (type === "extra") {
    itemsToPrint = itemsToPrint.filter(
      (item) => item.type === "extra" && item.activity !== "Rafting"
    );
  }
  if (type === "custom") {
    itemsToPrint = activities;
  }

  const isActivityPrint =
    type === "activity" || type === "extra" || type === "custom";
  const subtotal = isActivityPrint
    ? (itemsToPrint || []).reduce(
        (total, item) => total + Number(item.price || 0) * Number(item.qty || 0),
        0
      )
    : booking.total_price;

  const page = `
    ${renderInvoiceHeader()}
    <table class="detail-table">
      <tbody>
        ${renderConfiguredIdentityRows(booking, printMeta, user)}
      </tbody>
    </table>
    <div class="border-dashed"></div>
    <h2 class="track-title">${escapeHtml(getTrackLabel(booking))}</h2>
    ${renderInvoiceActivities(itemsToPrint, isActivityPrint)}
    <div class="border-dashed"></div>
    <table class="payment-table"><tbody>${renderPaymentRows(booking, subtotal)}</tbody></table>
    ${
      type === "invoice" || type === "custom"
        ? `
          <div class="border-dashed signature-divider"></div>
          <p class="center signature-title"><strong>Signature of Guest</strong></p>
          <div class="signature-space"></div>
          <p class="center">(${escapeHtml(getGuestName(booking))})</p>
        `
        : ""
    }
    <div class="border-dashed footer-divider"></div>
    <p class="center thank-you" style="font-size:${getLayoutSettings().footerFontSize}px"><strong>${escapeHtml(getLayoutSettings().footerText)}</strong></p>
  `;

  return wrapPrintHtml("Print Booking", invoiceCss, [page]);
};

const renderMealsVoucher = (meals) => `
  <div class="border-dashed"></div>
  <h3 class="title">Meals Voucher</h3>
  <table class="meal-voucher-table">
    <colgroup>
      <col style="width: 82%;" />
      <col style="width: 18%;" />
    </colgroup>
    <tr><th class="text-left">Item</th><th class="text-right">Qty</th></tr>
    ${(meals || [])
      .map(
        (item) => `
          <tr>
            <td class="meal-item">${escapeHtml(item.name || "-")}</td>
            <td class="meal-qty">${escapeHtml(item.qty || 0)}</td>
          </tr>
          ${
            item.note
              ? `<tr><td colspan="2" class="meal-note"><strong>Note:</strong> <em>${escapeHtml(item.note)}</em></td></tr>`
              : ""
          }
        `
      )
      .join("")}
  </table>
`;

const renderTicketIdentity = (booking, printMeta, user, includeHost = false) => `
  <table class="detail-table">
    ${renderConfiguredIdentityRows(booking, printMeta, user, includeHost)}
  </table>
`;

const buildTicketHtml = (booking, options, printMeta) => {
  const {
    user = { name: "Host" },
    meals = [],
    printTickets = { activity: true, meal: true },
    copyLabels,
  } = options;
  const allActivities = options.activities || booking.activities || [];
  const pages = [];
  const activityLabels = copyLabels || ["GUIDE", "RESTO", "CUSTOMER"];
  const mealLabels = copyLabels || ["RESTO", "CUSTOMER"];

  if (printTickets.activity) {
    activityLabels.forEach((label) => {
      pages.push(`
        ${renderTicketHeader(label)}
        <div class="border-dashed footer-divider"></div>
        <h3 class="title">TICKET</h3>
        ${renderTicketIdentity(booking, printMeta, user, true)}
        ${renderTicketActivities(allActivities)}
        <div class="border-dashed footer-divider"></div>
        <h3 class="title">Trip Option</h3>
        <p class="small">${escapeHtml(booking.trip?.name || "-")} - ${escapeHtml(
          booking.trip?.start_time || "-"
        )} - ${escapeHtml(booking.trip?.end_time || "-")}</p>
        <p class="track">${escapeHtml(getTrackLabel(booking))}</p>
        <div class="border-dashed"></div>
        <h3 class="title">Host</h3>
        <p>${escapeHtml(user?.name || "System")}</p>
        <p>______________________</p>
        ${printTickets.meal ? renderMealsVoucher(meals) : ""}
        <div class="border-dashed footer-divider"></div>
        <p class="ticket-footer thank-you" style="font-size:${getLayoutSettings().footerFontSize}px">${escapeHtml(getLayoutSettings().footerText)}</p>
      `);
    });
  } else if (printTickets.meal) {
    mealLabels.forEach((label) => {
      pages.push(`
        ${renderTicketHeader(label)}
        <div class="border-dashed"></div>
        ${renderTicketIdentity(booking, printMeta, user, true)}
        ${renderMealsVoucher(meals)}
        <div class="border-dashed footer-divider"></div>
        <p class="ticket-footer thank-you" style="font-size:${getLayoutSettings().footerFontSize}px">${escapeHtml(getLayoutSettings().footerText)}</p>
      `);
    });
  }

  return wrapPrintHtml("Print Ticket", ticketCss, pages);
};

const getCharsPerLine = () => {
  return getLayoutSettings().charsPerLine;
};

const toPlainText = (value) => String(value ?? "-").replace(/\s+/g, " ").trim();

const splitText = (value, width) => {
  const text = toPlainText(value);
  if (!text) return ["-"];

  const words = text.split(" ");
  const lines = [];
  let current = "";

  words.forEach((word) => {
    if (!current) {
      current = word;
      return;
    }

    if (`${current} ${word}`.length <= width) {
      current = `${current} ${word}`;
    } else {
      lines.push(current);
      current = word;
    }
  });

  if (current) lines.push(current);

  return lines.flatMap((line) => {
    if (line.length <= width) return [line];
    const chunks = [];
    for (let index = 0; index < line.length; index += width) {
      chunks.push(line.slice(index, index + width));
    }
    return chunks;
  });
};

const sentenceCaseLabel = (value) => {
  const text = toPlainText(value).trim().toLowerCase();
  return text ? `${text.charAt(0).toUpperCase()}${text.slice(1)}` : text;
};

const buildLegacyCommands = (booking, options, printMeta) => {
  const commands = [];
  const layoutSettings = getLayoutSettings();
  const charsPerLine = getCharsPerLine();
  const separator = "-".repeat(charsPerLine);
  const printerFontCommands = {
    font_a: "\x1B\x4D\x00",
    font_b: "\x1B\x4D\x01",
    font_c: "\x1B\x4D\x02",
  };
  const normalizedFont = String(layoutSettings.printerFont || "font_a")
    .replace("FontA", "font_a")
    .replace("FontB", "font_b")
    .replace("FontC", "font_c");
  // ESC M 0 dulu: printer yang tidak punya Font C mengabaikan ESC M 2, sehingga
  // Font B dari baris kecil sebelumnya akan terbawa ke baris berikutnya.
  const inlineFont = `${printerFontCommands.font_a}${
    printerFontCommands[normalizedFont] || printerFontCommands.font_a
  }`;
  // Title Font: >=15 mempertahankan layout lama (track besar), 10-14 track normal, <10 judul ikut kecil.
  const titleSize = commandSizeFor(layoutSettings.titleFontSize, "normal", { allowSmall: true });
  const headingSize = titleSize === "small" ? "small" : "normal";
  // Title & footer hanya ikut ukuran font; line height dan character spacing
  // detail tidak dipakai supaya tidak mepet/renggang.
  const fixedTextOptions = {
    disableCharacterSpacing: true,
    lineHeight: DEFAULT_PRINT_LAYOUT_SETTINGS.lineHeight,
  };
  // Kapasitas Font B per baris: 384 dot / 9 dot = 42, 576 dot / 9 dot = 64.
  const smallCharsPerLine = getPaperConfig().width >= 576 ? 64 : 42;
  // size "large" di wrapper = lebar ganda, jadi satu baris hanya muat setengahnya.
  const largeCharsPerLine = Math.floor(charsPerLine / 2);
  // Lebar baris untuk teks tengah satu ukuran (nama, alamat, footer).
  const widthForSize = (size) => {
    if (size === "small") return Math.max(charsPerLine, smallCharsPerLine);
    if (size === "large") return largeCharsPerLine;
    return charsPerLine;
  };
  const spacing = Math.min(4, Math.max(0, Number(layoutSettings.characterSpacing) || 0));
  const detailSpacing = Math.min(1, spacing);
  const detailSpacingCommand = `\x1B\x20${String.fromCharCode(detailSpacing)}`;
  const noSpacingCommand = "\x1B\x20\x00";
  const inlineBoldOn = "\x1B\x45\x01";
  const inlineBoldOff = "\x1B\x45\x00";
  // Wrapper thermal mengabaikan CSS; satu feed kecil menjaga keterbacaan
  // tanpa mengembalikan jarak lebar pada versi sebelumnya.
  const microFeedDots = Math.min(1, Math.round(layoutSettings.rowGap));
  const inlineMicroFeed = microFeedDots > 0 ? `\x1B\x4A${String.fromCharCode(microFeedDots)}` : "";

  const addText = (value, config = {}) => {
    const isLarge = config.size === "large";
    // Spasi karakter ikut dilipatgandakan pada lebar ganda, jadi baris besar tanpa spasi.
    const textSpacing = isLarge
      ? 0
      : config.characterSpacingOverride ?? (config.disableCharacterSpacing ? 0 : spacing);
    // Wrapper lama mengabaikan size "small", jadi Font B juga disisipkan langsung di value.
    const textFont = config.size === "small" ? printerFontCommands.font_b : inlineFont;
    const lines = isLarge ? splitText(value, largeCharsPerLine) : [String(value ?? "")];
    lines.forEach((line) => {
      commands.push({
        type: "text",
        value: `${textFont}\x1B\x20${String.fromCharCode(textSpacing)}${line}`,
        align: config.align || "left",
        font: config.font,
        size: config.size,
        characterSpacing: textSpacing,
        lineHeight: config.lineHeight || commandLineHeight(),
      });
    });
  };
  const addBreak = (count = 1) => {
    Array.from({ length: count }).forEach(() =>
      commands.push({ type: "line_break" })
    );
  };
  const addSeparator = () => addText(separator, {
    align: "center",
    disableCharacterSpacing: true,
  });
  const addKeyValue = (label, value, options = {}) => {
    const { emphasizeLabel, spaced, boldLabel, microGap, ...printOptions } = options;
    const plainLabel = toPlainText(label);
    const labelText = `${sentenceCaseLabel(plainLabel)}:`;

    if (printOptions.size === "large") {
      // Baris besar: satu baris bila label + nilai muat di setengah lebar,
      // selain itu label di atas (kiri) dan nilai di bawah (kanan).
      const valueText = toPlainText(value);
      const lineHeight = printOptions.lineHeight || commandLineHeight();
      if (labelText.length + 1 + valueText.length <= largeCharsPerLine) {
        const padding = " ".repeat(largeCharsPerLine - labelText.length - valueText.length);
        const styledLabel = boldLabel ? `${inlineBoldOn}${labelText}${inlineBoldOff}` : labelText;
        commands.push({
          type: "text",
          value: `${inlineFont}\x1B\x20\x00${styledLabel}${padding}${valueText}`,
          align: "left",
          font: printOptions.font,
          size: "large",
          characterSpacing: 0,
          lineHeight,
        });
      } else {
        addText(labelText, {
          align: "left",
          font: boldLabel ? "bold" : printOptions.font,
          size: "large",
          lineHeight,
        });
        addText(valueText, { align: "right", font: printOptions.font, size: "large", lineHeight });
      }
      if (spaced) addBreak();
      return;
    }
    // Keep the label column proportional to the actual label so short labels
    // do not waste printable width before the value column.
    const currentLabelWidth = Math.min(Math.max(labelText.length + 1, 6), charsPerLine - 8);
    const currentValueWidth = charsPerLine - currentLabelWidth;
    const valueLines = splitText(value, currentValueWidth);
    const lineOptions = { ...printOptions };

    valueLines.forEach((line, index) => {
      const labelPart = index === 0 ? labelText : "";
      const padding = index === 0
        ? " ".repeat(Math.max(0, currentLabelWidth - labelText.length))
        : " ".repeat(currentLabelWidth);
      const styledLabel =
        index === 0 && boldLabel
          ? `${inlineBoldOn}${labelPart}${inlineBoldOff}`
          : labelPart;
      const rightPadding = " ".repeat(Math.max(0, currentValueWidth - line.length));
      const inlineValue = `${styledLabel}${noSpacingCommand}${padding}${noSpacingCommand}${rightPadding}${detailSpacingCommand}${line}${microGap ? inlineMicroFeed : ""}`;
      commands.push({
        type: "text",
        // Fallback for older wrappers that only understand a plain text value.
        value: `${inlineFont}\x1B\x20\x00${inlineValue}`,
        columns: [
          {
            value: labelPart,
            width: currentLabelWidth,
            align: "left",
            font: boldLabel ? "bold" : undefined,
          },
          {
            value: line,
            width: currentValueWidth,
            align: "right",
            font: lineOptions.font,
            size: lineOptions.size,
          },
        ],
        align: lineOptions.align || "left",
        font: lineOptions.font,
        size: lineOptions.size,
        characterSpacing: detailSpacing,
        lineHeight: lineOptions.lineHeight || commandLineHeight(),
      });
    });

    if (spaced) addBreak();
  };
  const addMealLine = (name, qty, options = {}) => {
    const qtyText = toPlainText(qty || 0);
    const qtyWidth = Math.max(4, qtyText.length);
    const nameWidth = charsPerLine - qtyWidth - 1;
    const nameLines = splitText(name || "-", nameWidth);

    nameLines.forEach((line, index) => {
      const right = index === 0 ? qtyText : "";
      const left = line;
      const fallback = `${inlineFont}\x1B\x20\x00${left.padEnd(nameWidth, " ")} ${right.padStart(qtyWidth, " ")}`;
      commands.push({
        type: "text",
        value: fallback,
        columns: [
          {
            value: left,
            width: nameWidth,
            align: "left",
            font: options.font,
          },
          {
            value: right,
            width: qtyWidth,
            align: "right",
            font: options.font,
          },
        ],
        align: "left",
        font: options.font,
        size: options.size,
        characterSpacing: detailSpacing,
        lineHeight: options.lineHeight || commandLineHeight(),
      });
    });
  };
  const addHeader = (copyLabel = "", compact = false) => {
    const layout = getLayoutSettings();
    if (copyLabel) {
      addText(copyLabel, { ...fixedTextOptions, align: "center", font: "bold", size: headingSize });
    }

    const logoPayload = getLogoBase64Payload();
    if (logoPayload) {
      commands.push({
        type: "image",
        value: logoPayload,
        align: "center",
        width: FORCED_LOGO_WIDTH,
        format: "base64",
      });
      addBreak();
    }

    // Nama & alamat seperti title/footer: hanya ikut ukuran font, tidak ikut
    // line height / character spacing detail.
    const companyNameSize = commandSizeFor(layout.headerFontSize, "normal", { allowSmall: true });
    splitText(layout.companyName, widthForSize(companyNameSize)).forEach((line) =>
      addText(line, { ...fixedTextOptions, align: "center", font: "bold", size: companyNameSize })
    );
    const companyAddressSize = commandSizeFor(layout.companyFontSize, "normal", { allowSmall: true });
    splitText(layout.companyAddress, widthForSize(companyAddressSize)).forEach((line) =>
      addText(line, { ...fixedTextOptions, align: "center", size: companyAddressSize })
    );
    addSeparator();
  };
  const addBookingIdentity = (includeHost = true) => {
    const values = {
      host: (options.user?.name || "System").toUpperCase(),
      code: booking.code || "-",
      date: booking.reservation_date || "-",
      print_date: printMeta.date,
      print_time: printMeta.time,
      guest: getGuestName(booking),
      hotel: getHotelName(booking),
      agent: booking.agent?.name || "-",
      driver: booking.driver || "-",
      transport: booking.guest_transport || "-",
    };
    const fallbackFields = Object.keys(values).map((id) => ({ id, label: id.replace("_", " "), visible: true, bold: true }));
    const configuredFields = getLayoutSettings().fields || fallbackFields;
    configuredFields.forEach((field) => {
      if (!field.visible || !values[field.id] || (field.id === "host" && !includeHost)) return;
      addKeyValue(field.label || field.id, values[field.id], {
        boldLabel: field.bold !== false,
        emphasizeLabel: true,
        lineHeight: commandLineHeight(),
        size: commandSizeFor(field.fontSize, "normal"),
      });
    });
  };
  const addInvoiceActivities = (activities, showAmount = false) => {
    const itemHeader = getConfiguredItemStyle("itemHeader");
    const itemName = getConfiguredItemStyle("itemName");
    const itemQty = getConfiguredItemStyle("itemQty");
    addSeparator();
    addText(getTrackLabel(booking), { ...fixedTextOptions, align: "center", font: "bold", size: titleSize });
    addBreak();
    if (itemHeader?.visible !== false) {
      addKeyValue("Activity", showAmount ? "Qty / Amount" : "Qty", {
        font: itemHeader?.bold === false ? undefined : "bold",
      });
    }

    (activities || []).forEach((activity) => {
      if (itemName?.visible === false || itemQty?.visible === false) return;
      const amount = Number(activity.price || 0) * Number(activity.qty || 0);
      addKeyValue(
        getActivityName(activity),
        showAmount ? `${activity.qty || 0} / ${formatMoney(amount)}` : activity.qty || 0,
        {
          font: itemName?.bold === false ? undefined : "bold",
          size: commandSizeFor(itemName?.fontSize, "normal"),
        }
      );
      addBreak();
    });
  };
  const addTicketActivities = (activities) => {
    addSeparator();
    addText("Main Activities", { align: "center", font: "bold" });
    addKeyValue("Activity", "Qty", { font: "bold" });
    (activities || []).forEach((activity) => {
      addKeyValue(getActivityName(activity), activity.qty || 0);
    });
  };
  const addPayment = (subtotal) => {
    addSeparator();
    const totalPaidStyle = getConfiguredItemStyle("totalPaid");
    const paymentOptions = { boldLabel: true, emphasizeLabel: true, lineHeight: commandLineHeight() };
    addKeyValue("Total Guest", booking.guest_pax || "-", paymentOptions);

    if (booking.payment_splits?.length) {
      addKeyValue("Payment Type", "SPLIT PAYMENT", paymentOptions);
      let totalPaid = 0;

      booking.payment_splits.forEach((split, index) => {
        const baseAmount = parseInt(split.amount || 0);
        const taxAmount = (baseAmount * parseInt(split.tax || 0)) / 100;
        const totalAmount = baseAmount + taxAmount;
        totalPaid += totalAmount;
        addKeyValue(`Payment #${index + 1}`, split.payment_method?.name || "Unknown", paymentOptions);
        addKeyValue("Amount", formatMoney(totalAmount), paymentOptions);
      });

      addKeyValue("Total Paid", formatMoney(totalPaid), {
        ...paymentOptions,
      });
      return;
    }

    const taxPercentage = parseInt(booking.tax || 0);
    const taxAmount = (parseInt(subtotal || 0) * taxPercentage) / 100;
    const totalPaid = parseInt(subtotal || 0) + taxAmount;

    addKeyValue("Payment Method", booking.payment_method?.name || "-", {
      ...paymentOptions,
    });
    addKeyValue("Sub Total", formatMoney(subtotal), paymentOptions);
    addKeyValue(`Tax ${taxPercentage || "-"}%`, formatMoney(taxAmount), paymentOptions);
    if (totalPaidStyle?.visible !== false) {
      addKeyValue("Total Paid", formatMoney(totalPaid), {
        ...paymentOptions,
        boldLabel: totalPaidStyle?.bold !== false,
        size: commandSizeFor(totalPaidStyle?.fontSize, "normal"),
      });
    }
  };
  const addSignature = () => {
    addSeparator();
    addBreak();
    addText("Signature of Guest", { align: "center", font: "bold" });
    addBreak(4);
    addText(`(${getGuestName(booking)})`, { align: "center" });
  };
  const addFooter = (message = "Thank you for using our service!", compact = false) => {
    const layout = getLayoutSettings();
    const footerText = layout.footerText || message;
    addSeparator();
    const footerSize = commandSizeFor(layout.footerFontSize, "normal", { allowSmall: true });
    // Font kecil lebih sempit, jadi footer dimuatkan ke satu baris bila cukup.
    splitText(footerText, widthForSize(footerSize)).forEach((line) => {
      addText(line, {
        ...fixedTextOptions,
        align: "center",
        size: footerSize,
      });
    });
    commands.push({ type: "cut" });
  };
  const addMealsVoucher = () => {
    const mealName = getConfiguredItemStyle("mealName");
    const mealQty = getConfiguredItemStyle("mealQty");
    addSeparator();
    addText("Meals Voucher", { ...fixedTextOptions, align: "center", font: "bold", size: headingSize });
    if (mealName?.visible !== false && mealQty?.visible !== false) {
      addMealLine("Item", "Qty", {
        font: mealName?.bold === false && mealQty?.bold === false ? undefined : "bold",
      });
    }
    (options.meals || []).forEach((meal) => {
      if (mealName?.visible === false || mealQty?.visible === false) return;
      addMealLine(meal.name || "-", meal.qty || 0);
      if (meal.note) addText(`Note: ${meal.note}`, { size: "small" });
      addSeparator();
    });
  };
  const addTicketCopy = (label, config) => {
    addHeader(label, true);
    addText("TICKET", { ...fixedTextOptions, align: "center", font: "bold", size: headingSize });
    addKeyValue("HOST", (options.user?.name || "System").toUpperCase(), { emphasizeLabel: true });
    addKeyValue("Code", booking.code || "-", { emphasizeLabel: true });
    addKeyValue("Date", booking.reservation_date || "-", { emphasizeLabel: true });
    addKeyValue("Print Date", printMeta.date, { emphasizeLabel: true });
    addKeyValue("Print Time", printMeta.time, { emphasizeLabel: true });
    addKeyValue("Guest", booking.customer?.name || getGuestName(booking), { emphasizeLabel: true });
    addKeyValue("Hotel", getHotelName(booking), { emphasizeLabel: true });
    addKeyValue("Total Pax", booking.guest_pax || "-", { emphasizeLabel: true });

    if (config.activity) {
      addTicketActivities(config.activities);
      addSeparator();
      addText("Trip Option", { align: "center", font: "bold" });
      addText(
        `${booking.trip?.name || "-"} - ${booking.trip?.start_time || "-"} - ${booking.trip?.end_time || "-"}`,
        { align: "center", size: "small" }
      );
      addText(getTrackLabel(booking), {
        ...fixedTextOptions,
        align: "center",
        font: "bold",
        size: titleSize,
      });
      addBreak();
      addSeparator();
      addText("Host", { align: "center", font: "bold" });
      addText(options.user?.name || "System", { align: "center" });
      addText("______________________", { align: "center" });
    }

    if (config.meal) addMealsVoucher();
    addFooter("Thank you for choosing our service!", true);
  };

  if (options.type === "ticket") {
    const printTickets = options.printTickets || { activity: true, meal: true };
    const ticketActivities = options.activities || booking.activities || [];
    const activityLabels = options.copyLabels || ["GUIDE", "RESTO", "CUSTOMER"];
    const mealLabels = options.copyLabels || ["RESTO", "CUSTOMER"];

    if (printTickets.activity) {
      activityLabels.forEach((label) =>
        addTicketCopy(label, {
          activity: true,
          meal: printTickets.meal,
          activities: ticketActivities,
        })
      );
    } else if (printTickets.meal) {
      mealLabels.forEach((label) =>
        addTicketCopy(label, { activity: false, meal: true, activities: [] })
      );
    }

    return commands;
  }

  let itemsToPrint = booking.activities || [];
  if (options.type === "activity") {
    itemsToPrint = itemsToPrint.filter(
      (item) => item.type === "product" || item.activity === "Rafting"
    );
  }
  if (options.type === "extra") {
    itemsToPrint = itemsToPrint.filter(
      (item) => item.type === "extra" && item.activity !== "Rafting"
    );
  }
  if (options.type === "custom") {
    itemsToPrint = options.activities || [];
  }

  const isActivityPrint =
    options.type === "activity" ||
    options.type === "extra" ||
    options.type === "custom";
  const subtotal = isActivityPrint
    ? itemsToPrint.reduce(
        (total, item) => total + Number(item.price || 0) * Number(item.qty || 0),
        0
      )
    : booking.total_price;

  addHeader();
  addBookingIdentity();
  addInvoiceActivities(itemsToPrint, isActivityPrint);
  addPayment(subtotal);
  if (!options.type || options.type === "invoice" || options.type === "custom") {
    addSignature();
  }
  addFooter("Thank you for using our service!");

  return commands;
};

const buildRawEscPos = (commands) => {
  const ESC = "\x1B";
  const GS = "\x1D";
  const NEWLINE = "\n";
  const RESET = ESC + "@";
  const LEFT = ESC + "a" + String.fromCharCode(0);
  const CENTER = ESC + "a" + String.fromCharCode(1);
  const RIGHT = ESC + "a" + String.fromCharCode(2);
  const BOLD_ON = ESC + "E" + String.fromCharCode(1);
  const BOLD_OFF = ESC + "E" + String.fromCharCode(0);
  const NORMAL_SIZE = GS + "!" + String.fromCharCode(0);
  const LARGE_SIZE = GS + "!" + String.fromCharCode(17);
  const CUT = ESC + "d" + String.fromCharCode(3);

  return commands.reduce((raw, command) => {
    if (command.type === "line_break") return raw + NEWLINE;
    if (command.type === "cut") return raw + NEWLINE + CUT;
    if (command.type === "image") return raw;
    if (command.type !== "text") return raw;

    const align =
      command.align === "center" ? CENTER : command.align === "right" ? RIGHT : LEFT;
    const bold = command.font === "bold";
    const size = command.size === "large" ? LARGE_SIZE : NORMAL_SIZE;

    return (
      raw +
      align +
      size +
      (bold ? BOLD_ON : BOLD_OFF) +
      command.value +
      BOLD_OFF +
      NORMAL_SIZE +
      NEWLINE
    );
  }, RESET);
};

const printContent = async ({ html, css }, legacyCommands, paperConfig) => {
  const androidHandler =
    typeof window !== "undefined" ? window.AndroidPrintHandler : undefined;

  if (androidHandler && typeof androidHandler.printJsonData === "function") {
    androidHandler.printJsonData(
      JSON.stringify({
        ...paperConfig,
        commands: legacyCommands,
      })
    );
    return;
  }

  if (androidHandler && typeof androidHandler.printRawData === "function") {
    androidHandler.printRawData(buildRawEscPos(legacyCommands));
    return;
  }

  await preloadPrintImage(KUBER_LOGO_BASE64);

  printJS({
    printable: html,
    type: "raw-html",
    style: css,
  });
};

// options.layoutSettings: pakai setting ini apa adanya (Test Print dari editor), tanpa API.
export async function printBookingPOS(booking, options = {}) {
  const layoutSettings = options.layoutSettings
    ? normalizePrintLayoutSettings(options.layoutSettings)
    : await resolveLayoutSettings(apiPrintTypeFor(options));
  const printMeta = getPrintMeta();

  activeLayoutSettings = layoutSettings;
  let printData;
  let legacyCommands;
  let paperConfig;
  try {
    printData =
      options.type === "ticket"
        ? buildTicketHtml(booking, options, printMeta)
        : buildInvoiceHtml(booking, options, printMeta);
    legacyCommands = buildLegacyCommands(booking, options, printMeta);
    paperConfig = getPaperConfig();
  } finally {
    activeLayoutSettings = null;
  }

  return printContent(printData, legacyCommands, paperConfig);
}
